<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Loader2, Lock, Users, X } from 'lucide-svelte';

  import { account } from '$lib/stores/auth';
  import { maxListNameLength, SavedListRefused, type ListScope } from '$lib/api/savedlists';

  /**
   * Naming a list, and saying who may see it.
   *
   * One dialog for making and renaming, because they ask the same two
   * questions and the second one — the scope — is the part worth getting right
   * in one place rather than twice.
   *
   * # The scope is offered, not merely validated
   *
   * An account with no seat cannot make a shared list: the API answers
   * `NO_ORGANISATION`. Refusing after the fact would be a form that accepts an
   * answer and then rejects it, so the option is visibly unavailable with the
   * reason beside it.
   */

  interface Props {
    /**
     * Whether this is a rename rather than a new list.
     *
     * Scalar props rather than the list object, following `ConfirmDialog`: the
     * two below seed form fields *once*, and a prop the form kept reading
     * would fight the customer's typing. Naming them `initial` says which it
     * is at the call site.
     */
    isRename?: boolean;

    initialName?: string;
    initialScope?: ListScope;

    onSave: (_name: string, _scope: ListScope) => Promise<void>;
    onCancel: () => void;
  }

  let {
    isRename = false,
    initialName = '',
    initialScope = 'user',
    onSave,
    onCancel
  }: Props = $props();

  // Capturing the initial value is the point, which is what the checker is
  // warning about. The dialog is mounted fresh each time it opens, and a field
  // that kept following its prop would overwrite what the customer is typing.
  // svelte-ignore state_referenced_locally
  let name = $state(initialName);
  // svelte-ignore state_referenced_locally
  let scope = $state<ListScope>(initialScope);
  let busy = $state(false);
  let refusal = $state('');

  /** Whether this account holds a seat, which is what sharing needs. */
  const hasOrganisation = $derived(Boolean($account?.organisation));

  const tooLong = $derived([...name].length > maxListNameLength);
  const canSave = $derived(!busy && name.trim() !== '' && !tooLong);

  /**
   * Whether this rename takes a shared list private.
   *
   * Worth saying out loud: the API hands such a list to whoever did it, so a
   * colleague's shared list becomes *this* account's personal one. Surprising
   * enough that discovering it afterwards would be a bug report.
   */
  const takingItPrivate = $derived(isRename && initialScope === 'organisation' && scope === 'user');

  async function save() {
    if (!canSave) return;

    busy = true;
    refusal = '';
    try {
      await onSave(name.trim(), scope);
    } catch (error) {
      refusal = error instanceof SavedListRefused ? error.message : $_('savedLists.dialog.failed');
    } finally {
      busy = false;
    }
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onCancel();
  }
</script>

<svelte:window on:keydown={onKeydown} />

<div
  class="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm"
  role="presentation"
  onclick={onCancel}
>
  <!-- The panel swallows the click the backdrop uses to cancel, as ConfirmDialog does. -->
  <div
    class="mx-4 w-full max-w-md rounded-surface border border-gray-200 bg-white shadow-overlay"
    role="dialog"
    aria-modal="true"
    aria-label={isRename ? $_('savedLists.dialog.renameTitle') : $_('savedLists.dialog.newTitle')}
    tabindex="-1"
    onclick={(event) => event.stopPropagation()}
  >
    <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4">
      <h3 class="text-base font-semibold text-gray-900">
        {isRename ? $_('savedLists.dialog.renameTitle') : $_('savedLists.dialog.newTitle')}
      </h3>
      <button
        type="button"
        onclick={onCancel}
        class="rounded-control p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
      >
        <span class="sr-only">{$_('buttons.close')}</span>
        <X class="h-4 w-4" />
      </button>
    </div>

    <form
      onsubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div class="space-y-5 px-5 py-4">
        <div>
          <label for="saved-list-name" class="mb-1.5 block text-sm font-medium text-gray-700">
            {$_('savedLists.dialog.nameLabel')}
          </label>
          <!-- svelte-ignore a11y_autofocus -->
          <input
            id="saved-list-name"
            type="text"
            bind:value={name}
            autofocus
            maxlength={maxListNameLength}
            placeholder={$_('savedLists.dialog.namePlaceholder')}
            class="w-full rounded-control border border-hairline px-3 py-2 text-sm text-gray-900 outline-none focus:border-accent-400"
          />
        </div>

        <fieldset>
          <legend class="mb-1.5 text-sm font-medium text-gray-700">
            {$_('savedLists.dialog.scopeLabel')}
          </legend>
          <div class="space-y-2">
            <label
              class="flex cursor-pointer items-start gap-3 rounded-control border border-hairline px-3 py-2 transition-colors hover:bg-surface-canvas"
              class:border-accent-400={scope === 'user'}
            >
              <input type="radio" bind:group={scope} value="user" class="mt-1" />
              <span class="min-w-0">
                <span class="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                  <Lock class="h-3.5 w-3.5 text-gray-400" />
                  {$_('savedLists.scope.user')}
                </span>
                <span class="block text-xs text-gray-500">
                  {$_('savedLists.dialog.userHint')}
                </span>
              </span>
            </label>

            <label
              class="flex items-start gap-3 rounded-control border border-hairline px-3 py-2 transition-colors"
              class:cursor-pointer={hasOrganisation}
              class:hover:bg-surface-canvas={hasOrganisation}
              class:opacity-60={!hasOrganisation}
              class:border-accent-400={scope === 'organisation'}
            >
              <input
                type="radio"
                bind:group={scope}
                value="organisation"
                disabled={!hasOrganisation}
                class="mt-1"
              />
              <span class="min-w-0">
                <span class="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                  <Users class="h-3.5 w-3.5 text-gray-400" />
                  {$_('savedLists.scope.organisation')}
                </span>
                <span class="block text-xs text-gray-500">
                  {hasOrganisation
                    ? $_('savedLists.dialog.organisationHint')
                    : $_('savedLists.dialog.noOrganisation')}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {#if takingItPrivate}
          <p class="rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {$_('savedLists.dialog.takingItPrivate')}
          </p>
        {/if}

        {#if tooLong}
          <p class="text-xs text-red-600">
            {$_('savedLists.dialog.nameTooLong', { values: { max: maxListNameLength } })}
          </p>
        {/if}

        {#if refusal}
          <p class="text-xs text-red-600">{refusal}</p>
        {/if}
      </div>

      <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
        <button
          type="button"
          onclick={onCancel}
          class="rounded-control px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
        >
          {$_('buttons.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSave}
          class="inline-flex items-center gap-2 rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {#if busy}
            <Loader2 class="h-4 w-4 animate-spin" />
          {/if}
          {isRename ? $_('buttons.save') : $_('savedLists.dialog.create')}
        </button>
      </div>
    </form>
  </div>
</div>
